import { motion, MotionProps, Transition, type TargetAndTransition, type VariantLabels } from 'framer-motion'
import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '../../utils'

type ResizableDivProps = React.ComponentProps<'div'> &
  MotionProps & {
    children: React.ReactNode
    className?: string
    containerClassName?: string
    duration?: number
    initial?: TargetAndTransition | VariantLabels | boolean
  }

const transition: Transition = {
  type: 'spring' as const,
  stiffness: 300,
  damping: 30,
}

export const ResizableDiv = memo(
  forwardRef<HTMLDivElement, ResizableDivProps>(
    ({ children, className, containerClassName, duration, initial, ...props }, ref) => {
      const containerRef = useRef<HTMLDivElement | null>(null)
      const [size, setSize] = useState({ width: 0, height: 0 })

      const animation = useMemo(() => {
        if (duration) {
          return { duration }
        }
        return transition
      }, [duration])

      useEffect(() => {
        if (!containerRef.current) return

        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            setSize({
              width: entry.contentRect.width,
              height: entry.contentRect.height,
            })
          }
        })

        resizeObserver.observe(containerRef.current)

        return () => resizeObserver.disconnect()
      }, [])

      return (
        <motion.div
          ref={ref}
          initial={initial}
          animate={size}
          transition={animation}
          className={cn('relative', className)}
          {...props}
        >
          <div ref={containerRef} className={cn('h-fit w-fit', containerClassName)}>
            {children}
          </div>
        </motion.div>
      )
    },
  ),
)
ResizableDiv.displayName = 'ResizableDiv'
